import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, FolderSimple, CheckCircle, XCircle, Coins, FilePdf } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { StatusPill } from "../components/StatusPill";
import { CompanySwitcher } from "../components/CompanySwitcher";
import { api, errMsg, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const ReportPanel = ({ reportId, onClose, onChanged, isFreelancer }) => {
  const { user } = useAuth();
  const { t } = useT();
  const [report, setReport] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const isManager = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    api.get(`/reports/${reportId}`).then((r) => setReport(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [reportId]);

  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/reports/${reportId}/${action}`, { comment });
      toast.success(action === "approve" ? t("toast_reportApproved") : action === "reject" ? t("toast_reportRejected") : action === "cancel" ? t("toast_reportCancelled") : t("toast_reportReimbursed"));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/reports/${reportId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${report?.title || "expenses"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!report) return null;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/10 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full sm:w-[520px] lg:w-[40vw] bg-white border-l border-slate-200 shadow-[0_0_40px_rgba(0,0,0,0.08)] flex flex-col panel-slide" data-testid="report-panel">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="font-heading text-lg font-semibold text-slate-900 truncate">{report.title}</h2>
            {!isFreelancer && <StatusPill status={report.status} />}
          </div>
          <button onClick={onClose} data-testid="close-report-panel-btn" className="text-slate-400 hover:text-slate-900 transition-colors duration-150"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          <div className="border border-slate-200 grid grid-cols-3 divide-x divide-slate-200">
            <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("th_total")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5" data-testid="report-total">{fmtMoney(report.total, report.currency)}</p></div>
            <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("th_expenses")}</p><p className="font-mono font-semibold text-slate-900 mt-0.5">{report.expenses.length}</p></div>
            <div className="px-4 py-3"><p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">{t("by")}</p><p className="text-sm font-medium text-slate-900 mt-0.5 truncate">{report.user_name}</p></div>
          </div>

          <div className="border border-slate-200 divide-y divide-slate-100" data-testid="report-expenses-list">
            {report.expenses.map((e) => (
              <div key={e.id} className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{e.vendor || t("untitled")}</p>
                  <p className="font-mono text-[11px] text-slate-400">{e.date || "—"} · {t(`cat_${e.category}`)}</p>
                </div>
                <span className="font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(e.amount, e.currency)}</span>
              </div>
            ))}
          </div>

          {isManager && report.status === "submitted" && report.user_id !== user.id && (
            <div className="border border-slate-200 p-4 space-y-3 bg-slate-50">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{t("managerReview")}</p>
              <Textarea data-testid="report-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder={t("commentPh")} className="rounded-none bg-white" />
              <div className="flex gap-2">
                <Button data-testid="report-approve-btn" onClick={() => act("approve")} disabled={busy} className="rounded-none bg-emerald-600 hover:bg-emerald-700 flex-1"><CheckCircle size={16} className="mr-1" /> {t("approve")}</Button>
                <Button data-testid="report-reject-btn" onClick={() => act("reject")} disabled={busy} variant="destructive" className="rounded-none flex-1"><XCircle size={16} className="mr-1" /> {t("reject")}</Button>
              </div>
            </div>
          )}
          {isManager && report.status === "approved" && (
            <Button data-testid="report-reimburse-btn" onClick={() => act("reimburse")} disabled={busy} className="rounded-none w-full bg-indigo-600 hover:bg-indigo-700"><Coins size={16} className="mr-1" /> {t("markReimbursed")}</Button>
          )}

          {report.user_id === user.id && report.status === "submitted" && (
            <Button data-testid="report-cancel-btn" onClick={() => act("cancel")} disabled={busy} variant="outline" className="rounded-none w-full border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-300">
              <XCircle size={16} className="mr-1.5" /> {t("cancelReport")}
            </Button>
          )}

          {report.audit?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-2">{t("auditTrail")}</p>
              <div className="border border-slate-200 divide-y divide-slate-100">
                {[...report.audit].reverse().map((a, i) => (
                  <div key={i} className="px-3 py-2 text-xs text-slate-600 flex justify-between gap-2">
                    <span><span className="font-semibold capitalize">{a.action}</span> by {a.by}{a.comment ? ` — "${a.comment}"` : ""}</span>
                    <span className="font-mono text-slate-400 whitespace-nowrap">{new Date(a.at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-5 py-4">
          <Button data-testid="report-pdf-btn" onClick={downloadPdf} variant="outline" className="rounded-none w-full">
            <FilePdf size={16} className="mr-2" /> {t("downloadPdfBundle")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function ReportsPage({ workspace }) {
  const { t } = useT();
  const [reports, setReports] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [panel, setPanel] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const isFreelancer = workspace?.type === "freelancer";

  const load = useCallback(async () => {
    const [rep, ex] = await Promise.all([api.get("/reports"), api.get("/expenses")]);
    setReports(rep.data);
    setExpenses(ex.data);
    setLoaded(true);
  }, []);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const byMonth = (() => {
    const m = {};
    expenses.forEach((e) => {
      const k = (e.date || "").slice(0, 7);
      if (k) m[k] = (m[k] || 0) + e.amount;
    });
    return Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).slice(-6).map(([month, total]) => ({ month, total: Math.round(total * 100) / 100 }));
  })();

  const byCategory = (() => {
    const m = {};
    expenses.forEach((e) => { m[e.category] = (m[e.category] || 0) + e.amount; });
    return Object.entries(m).sort(([, a], [, b]) => b - a).slice(0, 6).map(([cat, total]) => ({ name: t(`cat_${cat}`), total: Math.round(total * 100) / 100 }));
  })();

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">{t("reports_title")}</h1>
        <p className="text-sm text-slate-500">{isFreelancer ? t("reports_sub_fl") : t("reports_sub")}</p>
      </div>

      <CompanySwitcher />

      {expenses.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="reports-charts">
          <div className="border border-slate-200 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">{t("byMonth")}</p>
            <div className="h-52" data-testid="chart-by-month">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byMonth} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="total" fill="#0f172a" radius={[2, 2, 0, 0]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="border border-slate-200 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">{t("byCategory")}</p>
            <div className="h-52" data-testid="chart-by-category">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byCategory} layout="vertical" margin={{ top: 4, right: 12, left: 10, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f8fafc" }} contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #e2e8f0" }} />
                  <Bar dataKey="total" fill="#3b82f6" radius={[0, 2, 2, 0]} maxBarSize={18} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {[t("th_report"), !isFreelancer && t("th_submittedBy"), t("th_expenses"), t("th_total"), !isFreelancer && t("th_status"), t("th_created")].filter(Boolean).map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="reports-table-body">
            {reports.map((r) => (
              <tr key={r.id} data-testid={`report-row-${r.id}`} onClick={() => setPanel(r.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors duration-150">
                <td className="px-3 py-2.5 font-medium text-slate-900">{r.title}</td>
                {!isFreelancer && <td className="px-3 py-2.5 text-slate-500">{r.user_name}</td>}
                <td className="px-3 py-2.5 font-mono text-slate-500">{r.expense_count}</td>
                <td className="px-3 py-2.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{fmtMoney(r.total, r.currency)}</td>
                {!isFreelancer && <td className="px-3 py-2.5"><StatusPill status={r.status} /></td>}
                <td className="px-3 py-2.5 font-mono text-xs text-slate-400 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loaded && reports.length === 0 && (
          <div className="py-20 flex flex-col items-center gap-3" data-testid="reports-empty-state">
            <FolderSimple size={40} className="text-slate-200" />
            <p className="font-heading font-semibold text-slate-900">{t("noReports")}</p>
            <p className="text-sm text-slate-500 max-w-sm text-center">{t("noReportsHint")}</p>
          </div>
        )}
      </div>

      {panel && <ReportPanel reportId={panel} onClose={() => setPanel(null)} onChanged={load} isFreelancer={isFreelancer} />}
    </div>
  );
}
